import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { formatDate } from '@/lib/format';

export interface ParentWeekControlsProps {
  /** The week on screen, `YYYY-MM-DD`, as the server drew it. Absent while loading. */
  weekStart?: string;
  weekEnd?: string;
  /** The day the parent picked, or empty for the week we are in. */
  pickedDate: string;
  /** Any day in the week wanted; empty means this week. */
  onChange: (weekOf: string) => void;
}

/** `2026-10-26` moved by `days`, without a timezone getting a say. */
function shiftDay(day: string, days: number): string {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/**
 * Which week of the child's timetable is on screen.
 *
 * A timetable is dated — the office can set one from 26 October — so a family
 * has to be able to reach that week: by picking the day, or a week at a time.
 * "This week" goes back to today.
 */
export function ParentWeekControls({ weekStart, weekEnd, pickedDate, onChange }: ParentWeekControlsProps) {
  const anchor = pickedDate || weekStart;

  return (
    <div className="flex flex-wrap items-end gap-2">
      <div className="w-full sm:w-44">
        <DatePicker
          label="Go to date"
          size="sm"
          value={pickedDate}
          placeholder="Choose a date"
          onChange={(event) => {
            if (event.target.value) onChange(event.target.value);
          }}
        />
      </div>

      <div className="flex items-center gap-1.5">
        <Button
          variant="secondary"
          size="sm"
          iconOnly
          aria-label="Previous week"
          title="Previous week"
          disabled={!anchor}
          onClick={() => anchor && onChange(shiftDay(anchor, -7))}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="min-w-[11rem] text-center text-sm font-semibold text-ink" aria-live="polite">
          {weekStart && weekEnd ? `${formatDate(weekStart)} – ${formatDate(weekEnd)}` : '—'}
        </span>
        <Button
          variant="secondary"
          size="sm"
          iconOnly
          aria-label="Next week"
          title="Next week"
          disabled={!anchor}
          onClick={() => anchor && onChange(shiftDay(anchor, 7))}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button variant="secondary" size="sm" disabled={!pickedDate} onClick={() => onChange('')}>
          This week
        </Button>
      </div>
    </div>
  );
}
